import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  IconArrowLeft,
  IconCalendarCheck,
  IconDeviceFloppy,
  IconRefresh,
} from "@tabler/icons-react";
import { useUser } from "contexts/v1/masterData/UserContext";
import { getRequest, postRequest } from "services/api";
import { URLS } from "utils/constants";
import "../public-holiday-quota-page.css";

const currentYear = new Date().getFullYear();

const getData = (response) => response?.data?.data || response?.data || [];

const toDateKey = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const formatDate = (value) => {
  const dateKey = toDateKey(value);
  if (!dateKey) return "-";
  return new Date(dateKey).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getDepartmentName = (user) =>
  (typeof user?.department === "string" ? user.department : user?.department?.name) ||
  user?.department_name ||
  user?.position?.department?.name ||
  "Tanpa Departemen";

const getUserFromAssignment = (assignment, users) =>
  (() => {
    const userFromContext = users.find((user) => String(user.id) === String(assignment.user_id));
    const userFromAssignment = assignment.user;

    // Prefer the full user record so department data matches the recap tab.
    if (userFromContext && userFromAssignment) {
      return {
        ...userFromAssignment,
        ...userFromContext,
        department: userFromContext.department || userFromAssignment.department,
        department_name: userFromContext.department_name || userFromAssignment.department_name,
      };
    }
    return userFromContext || userFromAssignment;
  })();

const createDemoData = (users = []) => {
  const fallbackUsers = [
    { id: "demo-1", name: "CONG NIK/SUBRATA WY.", department: { name: "PA DALAM" } },
    { id: "demo-2", name: "ILUH SUATI", department: { name: "PA DALAM" } },
    { id: "demo-3", name: "KARTINI", department: { name: "PA DALAM" } },
    { id: "demo-4", name: "MANG AYU", department: { name: "PA DALAM" } },
    { id: "demo-5", name: "SUSANA", department: { name: "PA LUAR" } },
    { id: "demo-6", name: "WEWET", department: { name: "PA LUAR" } },
    { id: "demo-7", name: "PUTU ANTARA/CETU", department: { name: "GUIDE" } },
    { id: "demo-8", name: "BUDI KURNIAWAN/MATEO", department: { name: "GUIDE" } },
  ];
  const assignedUsers = users.slice(0, 12).length ? users.slice(0, 12) : fallbackUsers;
  const holidayPackage = {
    id: "demo-package-use",
    name: `Public Holiday Staff Operasional ${currentYear}`,
    start_date: `${currentYear}-01-01`,
    end_date: `${currentYear}-12-31`,
    quota: 5,
    description: "Contoh input penggunaan PH per staff.",
  };

  return {
    packages: [holidayPackage],
    assignments: assignedUsers.map((user, index) => ({
      id: `demo-assignment-${user.id}`,
      holiday_package_id: holidayPackage.id,
      user_id: user.id,
      user,
      used_count: index % 3,
      remaining_quota: Math.max(5 - (index % 3), 0),
      dates: index % 2 === 0 ? [`${currentYear}-07-0${(index % 8) + 1}`] : [],
    })),
  };
};

const PHHubUseTab = ({ setActiveTab }) => {
  const { users } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(searchParams.get("package_id") || "");
  const [assignments, setAssignments] = useState([]);
  const [dateMatrix, setDateMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [isDemoData, setIsDemoData] = useState(false);

  const [collapsedDepts, setCollapsedDepts] = useState({});

  const toggleDept = (deptName) => {
    setCollapsedDepts((prev) => ({
      ...prev,
      [deptName]: !prev[deptName],
    }));
  };

  const selectedPackage = useMemo(
    () => packages.find((item) => String(item.id) === String(selectedPackageId)),
    [packages, selectedPackageId]
  );
  const quotaColumns = useMemo(() => {
    const quota = Number(selectedPackage?.quota || 5);
    return Array.from({ length: Math.max(quota, 1) }, (_, index) => index + 1);
  }, [selectedPackage?.quota]);
  const userList = useMemo(() => users.data || [], [users.data]);

  const loadDemoData = useCallback(() => {
    const demo = createDemoData(userList);
    setPackages(demo.packages);
    setSelectedPackageId(demo.packages[0].id);
    setAssignments(demo.assignments);
    setDateMatrix(
      demo.assignments.reduce((result, assignment) => ({
        ...result,
        [assignment.user_id]: assignment.dates || [],
      }), {})
    );
    setIsDemoData(true);
  }, [userList]);

  const loadPackages = useCallback(async () => {
    const response = await getRequest("/ahr/holiday-packages?all=1");
    const items = getData(response);
    setPackages(items);
    setIsDemoData(false);

    const nextPackageId = selectedPackageId || searchParams.get("package_id") || items[0]?.id || "";
    setSelectedPackageId(nextPackageId);
    if (nextPackageId) setSearchParams({ package_id: nextPackageId });
  }, [searchParams, selectedPackageId, setSearchParams]);

  const loadAssignments = useCallback(async (packageId) => {
    if (!packageId || isDemoData) return;

    const response = await getRequest(`/ahr/holiday-package-assignments?filter[holiday_package_id]=${packageId}&include=user,package&all=1`);
    const items = getData(response);
    setAssignments(items);
    
    let usedDatesByUser = {};
    const pkg = packages.find((p) => String(p.id) === String(packageId));
    
    if (pkg && pkg.start_date && pkg.end_date) {
      const startDate = String(pkg.start_date).slice(0, 10);
      const endDate = String(pkg.end_date).slice(0, 10);
      try {
        const schedResponse = await getRequest(`/ahr/schedules?all=1&filter[date_range][0]=${startDate}&filter[date_range][1]=${endDate}`);
        const fetchedSchedules = getData(schedResponse);
        const packageSchedules = fetchedSchedules.filter((s) => String(s.holiday_package_id) === String(packageId));
        
        usedDatesByUser = packageSchedules.reduce((acc, schedule) => {
           const uid = schedule.user_id;
           if (!acc[uid]) acc[uid] = [];
           const d = schedule.date || schedule.schedule_date;
           if (d) acc[uid].push(d);
           return acc;
        }, {});
      } catch (e) {
        console.error("Failed to fetch schedules", e);
      }
    }

    setDateMatrix((current) =>
      items.reduce((result, assignment) => {
        const userId = assignment.user_id || assignment.user?.id;
        return {
          ...result,
          [userId]: usedDatesByUser[userId] || current[userId] || [],
        };
      }, {})
    );
  }, [isDemoData, packages]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      await loadPackages();
    } catch (requestError) {
      loadDemoData();
      setError("Endpoint Holiday Package belum terbaca. Data contoh ditampilkan untuk preview input PH.");
    } finally {
      setLoading(false);
    }
  }, [loadDemoData, loadPackages]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedPackageId) loadAssignments(selectedPackageId);
  }, [loadAssignments, selectedPackageId]);

  const groupedAssignments = useMemo(() => {
    const groups = assignments.reduce((result, assignment) => {
      const user = getUserFromAssignment(assignment, userList);
      if (!user) return result;
      const departmentName = getDepartmentName(user).toUpperCase();
      return {
        ...result,
        [departmentName]: [...(result[departmentName] || []), { assignment, user }],
      };
    }, {});

    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [assignments, userList]);

  const updateDate = (userId, index, value) => {
    setDateMatrix((current) => {
      const currentDates = [...(current[userId] || [])];
      currentDates[index] = value;
      return { ...current, [userId]: currentDates };
    });
  };

  const submitUserDates = async (userId) => {
    const dates = (dateMatrix[userId] || []).map(toDateKey).filter(Boolean);
    if (!selectedPackage || !dates.length) return;

    setSaving(true);
    setError("");

    try {
      if (!isDemoData) {
        await postRequest(`/ahr/holiday-packages/${selectedPackage.id}/schedule-batch`, {
          status: "draft",
          user_ids: [userId],
          dates,
        });
        await loadAssignments(selectedPackage.id);
      }
    } catch (requestError) {
      setError(requestError.message || "Gagal menyimpan penggunaan PH.");
    } finally {
      setSaving(false);
    }
  };

  const submitAllDates = async () => {
    if (!selectedPackage) return;
    const payloads = Object.entries(dateMatrix)
      .map(([userId, dates]) => ({
        userId,
        dates: dates.map(toDateKey).filter(Boolean),
      }))
      .filter((item) => item.dates.length);

    setSaving(true);
    setError("");

    try {
      if (!isDemoData) {
        for (const item of payloads) {
          await postRequest(`/ahr/holiday-packages/${selectedPackage.id}/schedule-batch`, {
            status: "draft",
            user_ids: [item.userId],
            dates: item.dates,
          });
        }
        await loadAssignments(selectedPackage.id);
      }
    } catch (requestError) {
      setError(requestError.message || "Gagal menyimpan batch penggunaan PH.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="p-3 public-holiday-quota-page public-holiday-use-page">
        <div className="card border-0 shadow-sm mb-4 ph-toolbar ph-use-toolbar">
          <div className="card-body py-3">
            <div className="row g-3 align-items-end">
              <div className="col-md-5 col-lg-4">
                <label className="form-label text-muted small fw-bold text-uppercase mb-1">Pilih Package</label>
                <select
                  className="form-select"
                  value={selectedPackageId}
                  onChange={(event) => {
                    setSelectedPackageId(event.target.value);
                    if (event.target.value) setSearchParams({ package_id: event.target.value });
                  }}
                >
                  <option value="">Pilih package PH</option>
                  {packages.map((holidayPackage) => (
                    <option key={holidayPackage.id} value={holidayPackage.id}>{holidayPackage.name}</option>
                  ))}
                </select>
              </div>
              <div className="col">
                <div className="text-muted small">
                  Periode: <strong>{formatDate(selectedPackage?.start_date)} - {formatDate(selectedPackage?.end_date)}</strong> | Quota per staff: <strong>{selectedPackage?.quota || 0} hari</strong>
                </div>
              </div>
              <div className="col-12 col-lg-auto">
                <div className="btn-list justify-content-lg-end">
                  <button className="btn btn-light border" onClick={() => setActiveTab && setActiveTab("quota")}>
                    <IconArrowLeft size={16} className="me-1" /> Package PH
                  </button>
                  <button className="btn btn-light border" onClick={loadData} disabled={loading}>
                    <IconRefresh size={16} className="me-1" /> Refresh
                  </button>
                  <button className="btn btn-success" onClick={submitAllDates} disabled={saving || isDemoData || !selectedPackage}>
                    <IconDeviceFloppy size={16} className="me-1" /> Simpan Semua
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-warning d-flex align-items-center justify-content-between mb-4">
            <span>{error}</span>
            <button className="btn btn-sm btn-warning" onClick={() => setError("")}>Tutup</button>
          </div>
        )}

        {isDemoData && (
          <div className="ph-demo-banner mb-4">
            <span>DATA CONTOH</span> Data matrix hanya preview desain. Simpan aktif saat API terbaca.
          </div>
        )}

        <div className="card border-0 shadow-sm overflow-hidden">
          <div className="card-header d-flex align-items-center justify-content-between">
            <div>
              <h3 className="card-title mb-1">Matrix Penggunaan Public Holiday</h3>
              <div className="text-muted small">Input tanggal PH staff dengan tampilan matrix seperti spreadsheet.</div>
            </div>
          </div>
          <div className="table-responsive ph-use-scroll">
            <table className="ph-use-matrix mb-0">
              <thead>
                <tr>
                  <th className="ph-use-no">NO</th>
                  <th className="ph-use-name">NAMA</th>
                  {quotaColumns.map((column) => (
                    <th key={column} className="ph-use-date-col">{column}</th>
                  ))}
                  <th className="ph-use-action">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={quotaColumns.length + 3} className="text-center py-5">Memuat data PH...</td>
                  </tr>
                ) : groupedAssignments.length ? groupedAssignments.map(([departmentName, items]) => {
                  const isCollapsed = collapsedDepts[departmentName];
                  return (
                  <React.Fragment key={departmentName}>
                    <tr style={{ cursor: "pointer", backgroundColor: "#f8f9fa" }} onClick={() => toggleDept(departmentName)}>
                      <td className="ph-use-dept-spacer border-end-0" />
                      <td className="ph-use-dept border-start-0" colSpan={quotaColumns.length + 2}>
                        <div className="d-flex align-items-center gap-2 fw-bold text-primary">
                          {isCollapsed ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-chevron-right" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                              <polyline points="9 6 15 12 9 18"></polyline>
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-chevron-down" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          )}
                          {departmentName} ({items.length})
                        </div>
                      </td>
                    </tr>
                    {!isCollapsed && items.map(({ assignment, user }, index) => {
                      const userId = assignment.user_id || user.id;
                      const dates = dateMatrix[userId] || [];

                      return (
                        <tr key={assignment.id || userId}>
                          <td className="ph-use-no">{index + 1}</td>
                          <td className="ph-use-name fw-bold">{user.name}</td>
                          {quotaColumns.map((column, columnIndex) => (
                            <td key={column} className={dates[columnIndex] ? "ph-use-cell has-date" : "ph-use-cell"}>
                              <input
                                type="date"
                                value={toDateKey(dates[columnIndex])}
                                min={toDateKey(selectedPackage?.start_date)}
                                max={toDateKey(selectedPackage?.end_date)}
                                onChange={(event) => updateDate(userId, columnIndex, event.target.value)}
                              />
                            </td>
                          ))}
                          <td className="ph-use-action">
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              onClick={() => submitUserDates(userId)}
                              disabled={saving || isDemoData}
                            >
                              Simpan
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                )}) : (
                  <tr>
                    <td colSpan={quotaColumns.length + 3} className="text-center py-5">
                      Belum ada staff yang di-assign ke package ini.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default PHHubUseTab;
